/* eslint-disable no-param-reassign */
import { useCallback, useContext, useMemo } from 'react';
import { ClassNameMap } from '@mui/material';
import { isEmpty, toString } from 'lodash';
import { CalibrationPerformanceModel } from 'api';
import { GPC } from 'api/models/GPC';
import { globalAction } from 'common/actions';
import { performanceKeys } from 'common/constants/calibration';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { CompanyInfo } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import { ValuationsApproach } from 'common/types/valuation';
import { Calibration } from 'common/types/valuation/valuation';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import createColumnsCalibrationPerformance from 'pages/Valuations/approaches/guidelineCalibration/performance_metrics/config/createColumns/createColumnsCalibrationPerformance';
import rowConfigCalibrationPerformance from 'pages/Valuations/approaches/guidelineCalibration/performance_metrics/config/rowConfig/rowConfigCalibrationPerformance';
import { PublicComp } from 'pages/ValuationsAllocation/types';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { getObjectValue } from 'utilities';
import { Approach } from '../../guidelinePublicCompanies/types';
import {
  CustomCalibrationAttributes,
  GuidelinePublicCompaniesAttributesExtended,
} from '../../guidelinePublicCompanies/utils/types';
import useUpdateCustomDatePerformanceMetrics from '../../guidelinePublicCompanies/utils/useUpdateCustomDatePerformanceMetrics';
import rowConfigCalibrationOutputs from '../Outputs/rowConfigCalibrationOutputs';
import rowTransformerCalibrationOutputs from '../Outputs/rowTransformerCalibrationOutputs';
import { RowConfigCalibrationParams } from '../performance_metrics/config/rowConfig';
import useDisabledPerformances from '../performance_metrics/config/useDisabledPerformances';
import { getSelectorValues } from '../utilities';
import { getFinancials, PerformanceCalculator } from '../utils';

interface UseHandleChangeApproachProps {
  cell: ICell;
  calibration: Calibration;
  approaches: ValuationsApproach[];
  calibrationPerformanceSheet: any;
  calibrationOutputSheet: any;
  company: CompanyInfo;
  customClasses: ClassNameMap<string>;
  financialStatementsList: any;
}

const useHandleChangeApproach = ({
  cell,
  calibration,
  approaches,
  calibrationPerformanceSheet,
  calibrationOutputSheet,
  company,
  customClasses,
  financialStatementsList,
}: UseHandleChangeApproachProps) => {
  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  const { resetConfiguration, gpcAttributes, calibrationAttributes } = useContext(ValuationContext);
  const [format] = useFormat();

  const { getCompsWithCustomDatePerformanceMetricsCalibration, validateCustomDatePerformanceMetrics }
    = useUpdateCustomDatePerformanceMetrics({
      gpcComparison: [] as PublicComp[],
      gpcAttributes: gpcAttributes as unknown as GuidelinePublicCompaniesAttributesExtended,
      format,
      calibrationAttributes: calibrationAttributes as unknown as CustomCalibrationAttributes,
    });

  const [globalStore, dispatch] = useStore() as unknown as UseStoreValues;

  const { disabledIdentifiers, disabledNames } = useDisabledPerformances({ calibration });

  const orderMap = useMemo(() => {
    const performancesOrderMap = new Map();
    calibration?.performances?.forEach((item, index) => {
      performancesOrderMap.set(item.cap_iq_id, index);
    });

    return performancesOrderMap;
  }, [calibration]);

  const handleChange = useCallback(
    async (value?) => {
      const newValue = toString(value ?? calibration?.approach?.panelId);

      const approachNew = approaches.find(item => item.panelId === newValue);
      const selectedApproachNew = { ...approachNew };
      const { valuations_approach_gpc: valuationsApproachGpc } = getObjectValue(selectedApproachNew);
      calibration.approach = selectedApproachNew as Approach;
      calibration.gpc_reference = approachNew?.panelId;

      const financials = getFinancials(calibration.financials_version!, financialStatementsList);

      const asOfDate = calibration.calibration_date;

      let columnsPerformance = [];
      let rowConfig = [];

      if (
        calibration.calibration_date !== ''
        && (selectedApproachNew?.valuations_approach_gpc?.gpc_comparison ?? []).length > 0
      ) {
        dispatch(globalAction.showLoadingProgress(true));
        const tmpCompsWithCustomDatePerformanceMetrics = await getCompsWithCustomDatePerformanceMetricsCalibration(
          asOfDate,
          selectedApproachNew?.valuations_approach_gpc?.gpc_comparison as unknown as GPC[]
        );
        const compsWithValidatedCustomDatePerformanceMetrics = validateCustomDatePerformanceMetrics(
          tmpCompsWithCustomDatePerformanceMetrics as CalibrationPerformanceModel[]
        ) as Array<GPC>;
        // need to preserve enabled/disabled state
        const enabledUpdatedPerformances = compsWithValidatedCustomDatePerformanceMetrics?.map((comp: any) => {
          const wasDisabled = disabledIdentifiers?.includes(comp.cap_iq_id) || disabledNames?.includes(comp.name);
          return { ...comp, enabled: !wasDisabled };
        });
        enabledUpdatedPerformances.sort((a: any, b: any) => {
          const orderA = orderMap.has(a.cap_iq_id) ? orderMap.get(a.cap_iq_id) : calibration?.performances?.length;
          const orderB = orderMap.has(b.cap_iq_id) ? orderMap.get(b.cap_iq_id) : calibration?.performances?.length;
          return orderA - orderB;
        });
        const performanceMetricsAsOfDateCommonRowConfig = {
          asOfDate,
          company,
          customClasses,
          gpcComparisonByDate: !isEmpty(enabledUpdatedPerformances) ? enabledUpdatedPerformances : {},
        } as unknown as RowConfigCalibrationParams;

        columnsPerformance = createColumnsCalibrationPerformance({
          gpcComparisonByDate: !isEmpty(compsWithValidatedCustomDatePerformanceMetrics)
            ? compsWithValidatedCustomDatePerformanceMetrics
            : ({} as GPC[]),
          financials,
        });

        rowConfig = rowConfigCalibrationPerformance({
          ...performanceMetricsAsOfDateCommonRowConfig,
        });
      } else {
        const columns = {
          valuationsApproachGpc,
          financials,
        };

        rowConfig = rowConfigCalibrationPerformance({ company, valuationsApproachGpc });
        columnsPerformance = createColumnsCalibrationPerformance(columns);
      }

      const performanceMetricsKeys = columnsPerformance.filter(item =>
        Object.keys(item).some(key => key.includes('performance'))
      );

      const numRows = performanceMetricsKeys.length;
      const numCols = Object.keys(performanceMetricsKeys[0]).length;
      const idCalibration = calibration.id;
      calibration.performances = [];

      calibration.performances = PerformanceCalculator({
        numCols,
        numRows,
        performanceMetricsKeys,
        performanceKeys,
        idCalibration: idCalibration!,
        calibration,
      });
      columnsPerformance.splice(0, 1);
      calibrationPerformanceSheet.reset({
        rowConfig,
        columns: columnsPerformance,
      });

      const rowConfigOutputs = rowConfigCalibrationOutputs({
        calibration,
        columnsPerformance,
      });

      const columnsOutputs = rowTransformerCalibrationOutputs(getSelectorValues(calibration));

      calibrationOutputSheet.reset({
        rowConfig: rowConfigOutputs,
        columns: columnsOutputs,
      });

      if (!isEmpty(cell)) {
        onCellsChanged([{ cell, value: newValue }]);
      }

      resetConfiguration?.();
      dispatch(globalAction.showLoadingProgress(false));
    },
    [
      approaches,
      calibration,
      calibrationOutputSheet,
      calibrationPerformanceSheet,
      cell,
      company,
      customClasses,
      disabledIdentifiers,
      disabledNames,
      dispatch,
      financialStatementsList,
      getCompsWithCustomDatePerformanceMetricsCalibration,
      onCellsChanged,
      orderMap,
      resetConfiguration,
      validateCustomDatePerformanceMetrics,
    ]
  );

  return { handleChange };
};

export default useHandleChangeApproach;
